import {
  EMPTY_ARRAY,
  type BlotterTableFilter,
  type FilterBuilderSide,
  type FilterableProperty,
  type UseFilterBuilderOutput,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import { useOptionalSpaceParameterOpenClause, type OpenSpaceParameterClause } from './SpaceParameterOpenClauseProvider';
import { useOptionalSpaceParameters } from './SpaceParametersProvider';
import type {
  SpaceParameterKey,
  SpaceParameterPanelValueKey,
  SpaceParameterPanelValueSupport,
  SpaceParameterValues,
} from './types';
import {
  filterKeyToSpaceParameterKey,
  mergeSpaceParameterValues,
  omitControlledFilterBuilderProps,
  removeSpaceParameterKeys,
} from './utils';

function useStableParameterKeys(keys: readonly SpaceParameterKey[]) {
  const signature = keys.join('|');
  const stableKeysRef = useRef(keys);
  const signatureRef = useRef(signature);
  if (signatureRef.current !== signature) {
    signatureRef.current = signature;
    stableKeysRef.current = keys;
  }
  return stableKeysRef.current;
}

export interface UseSpaceParameterizedFilterOutput<TFilter extends BlotterTableFilter> {
  /**
   * Effective filter, combining local filter with space parameter controlled filter.
   */
  filter: TFilter;
  changeFilter: (action: SetStateAction<TFilter>) => void;
  /**
   * The keys that are controlled by space parameters, thus ok to omit from local filter.
   */
  controlledKeys: readonly string[];
  getSelfManagedFilterBuilderProps: (filterBuilderProps: UseFilterBuilderProps) => UseFilterBuilderProps;
}

type CheckedSpaceParameterPanelValues<
  TSupport extends SpaceParameterPanelValueSupport,
  TFilter extends BlotterTableFilter,
> =
  Exclude<SpaceParameterPanelValueKey<TSupport>, Extract<keyof TFilter, string>> extends never
    ? TSupport
    : TSupport & {
        readonly __missingSpaceParameterPanelValueKeys: Exclude<
          SpaceParameterPanelValueKey<TSupport>,
          Extract<keyof TFilter, string>
        >;
      };

export function useSpaceParameterizedFilter<
  TFilter extends BlotterTableFilter,
  const TSupport extends SpaceParameterPanelValueSupport,
>({
  filter: localFilter,
  changeFilter: changeLocalFilter,
  spaceParameterPanelValues,
}: {
  filter: TFilter;
  changeFilter: (action: SetStateAction<TFilter>) => void;
  spaceParameterPanelValues: CheckedSpaceParameterPanelValues<TSupport, NoInfer<TFilter>>;
}): UseSpaceParameterizedFilterOutput<TFilter> {
  const context = useOptionalSpaceParameters();
  const controlledKeys = context?.active ? spaceParameterPanelValues.keys : EMPTY_ARRAY;
  const { parameterToPanelValues } = spaceParameterPanelValues;
  const values = context?.values;

  useEffect(() => {
    if (controlledKeys.length === 0) {
      return;
    }
    changeLocalFilter(currentFilter =>
      removeSpaceParameterKeys(currentFilter as TFilter, controlledKeys, parameterToPanelValues)
    );
  }, [changeLocalFilter, controlledKeys, parameterToPanelValues]);

  const filter = useMemo(
    () => mergeSpaceParameterValues(localFilter, values ?? {}, controlledKeys, parameterToPanelValues),
    [controlledKeys, localFilter, parameterToPanelValues, values]
  );

  const changeFilter = useCallback(
    (action: SetStateAction<TFilter>) => {
      changeLocalFilter(currentFilter => {
        const nextFilter = action instanceof Function ? action(currentFilter) : action;
        return removeSpaceParameterKeys(nextFilter as TFilter, controlledKeys, parameterToPanelValues);
      });
    },
    [changeLocalFilter, controlledKeys, parameterToPanelValues]
  );

  const getSelfManagedFilterBuilderProps = useCallback(
    (filterBuilderProps: UseFilterBuilderProps) =>
      omitControlledFilterBuilderProps(filterBuilderProps, controlledKeys, parameterToPanelValues),
    [controlledKeys, parameterToPanelValues]
  );

  return {
    changeFilter,
    controlledKeys,
    getSelfManagedFilterBuilderProps,
    filter,
  };
}

type FilterablePropertyKey<TFilterableProperties> =
  NonNullable<TFilterableProperties> extends readonly (infer TProperty)[]
    ? TProperty extends FilterableProperty<infer TKey>
      ? Extract<TKey, string>
      : never
    : never;

export type SpaceParameterCompatibleFilterableProperties<
  TSupport extends SpaceParameterPanelValueSupport,
  TFilterableProperties,
> =
  string extends FilterablePropertyKey<TFilterableProperties>
    ? TFilterableProperties & { readonly __filterablePropertyKeysMustBeLiteral: never }
    : Exclude<SpaceParameterPanelValueKey<TSupport>, FilterablePropertyKey<TFilterableProperties>> extends never
      ? TFilterableProperties
      : TFilterableProperties & {
          readonly __missingSpaceParameterPanelValueKeys: Exclude<
            SpaceParameterPanelValueKey<TSupport>,
            FilterablePropertyKey<TFilterableProperties>
          >;
        };

/**
 * Verifies that every space parameter panel value key is provided by the table and returns the validated properties
 * for use by that table.
 */
export function useSpaceParameterControlledFilter<
  const TSupport extends SpaceParameterPanelValueSupport,
  const TFilterableProperties extends readonly (FilterableProperty | null | undefined)[] | undefined,
  TFilter extends BlotterTableFilter = BlotterTableFilter,
>(
  spaceParameterPanelValues: TSupport,
  filterableProperties: SpaceParameterCompatibleFilterableProperties<TSupport, TFilterableProperties>
) {
  const context = useOptionalSpaceParameters();
  const controlledKeys = context?.active ? spaceParameterPanelValues.keys : EMPTY_ARRAY;
  const { parameterToPanelValues } = spaceParameterPanelValues;
  const controlledFilterKeys = useMemo(
    () =>
      controlledKeys.flatMap(key => {
        const binding = parameterToPanelValues[key];
        return binding == null ? [] : [...binding.panelValueKeys];
      }),
    [controlledKeys, parameterToPanelValues]
  );
  const controlledFilter = useMemo(
    () => mergeSpaceParameterValues({} as TFilter, context?.values ?? {}, controlledKeys, parameterToPanelValues),
    [context?.values, controlledKeys, parameterToPanelValues]
  );
  const externallyControlledFilter = useMemo(
    () => ({ keys: controlledFilterKeys, values: controlledFilter }),
    [controlledFilter, controlledFilterKeys]
  );

  return { controlledFilter, controlledFilterKeys, externallyControlledFilter, filterableProperties };
}

export function useSpaceParameterOpenClause(
  localOpenClause: UseFilterBuilderOutput['addAndOpenClause'],
  spaceParameterPanelValues: SpaceParameterPanelValueSupport
) {
  const context = useOptionalSpaceParameters();
  const openClauseContext = useOptionalSpaceParameterOpenClause();
  const stableSupportedKeys = useStableParameterKeys(spaceParameterPanelValues.keys);
  const { parameterToPanelValues } = spaceParameterPanelValues;

  return useCallback(
    (property: string | undefined, side?: FilterBuilderSide, value?: string | string[]) => {
      const parameterKey = filterKeyToSpaceParameterKey(property, stableSupportedKeys, parameterToPanelValues);
      if (context?.active && openClauseContext != null && parameterKey != null) {
        openClauseContext.openClause(parameterKey, side, value);
        return;
      }
      localOpenClause(property, side, value);
    },
    [context?.active, localOpenClause, openClauseContext, parameterToPanelValues, stableSupportedKeys]
  );
}

export function useSpaceParameterController(controllerId: string, initialValues: SpaceParameterValues | undefined) {
  const context = useOptionalSpaceParameters();
  const initialValuesRef = useRef(initialValues ?? {});
  const registerController = context?.registerController;
  const [localValues, setLocalValues] = useState<SpaceParameterValues>(initialValuesRef.current);

  useEffect(() => {
    const unregisterController = registerController?.(controllerId, initialValuesRef.current);
    return () => {
      unregisterController?.();
    };
  }, [controllerId, registerController]);

  return {
    active: context?.active ?? true,
    availableKeys: context?.availableKeys ?? [],
    setValues: context?.setValues ?? setLocalValues,
    values: context?.values ?? localValues,
  };
}

export function useRegisterSpaceParameterOpenClause(controllerId: string, openClause: OpenSpaceParameterClause) {
  const context = useOptionalSpaceParameterOpenClause();
  const openClauseRef = useRef(openClause);
  openClauseRef.current = openClause;
  const registerOpenClause = context?.registerOpenClause;

  const stableOpenClause = useCallback<OpenSpaceParameterClause>(
    (property, side, value) => openClauseRef.current(property, side, value),
    []
  );

  useEffect(
    () => registerOpenClause?.(controllerId, stableOpenClause),
    [controllerId, registerOpenClause, stableOpenClause]
  );
}
